import { useEffect, useState, useRef, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useAudioContext } from "../hooks/useAudioContext";
import { playTypewriterClick } from "../utils/audioHelpers";
import { ANIMATION_DURATIONS } from "../constants/animations";
import {
  parseHeader,
  getHeaderClassName,
  stripMarkdownSyntax,
  renderTextWithFormatting,
  splitIntoPhrases,
  splitIntoWords,
  parseInlineMarkdown,
  renderHighlightRuns,
} from "../utils/textFormatting";
import { NUMBER_PILL_CLASS, numberPillStyle } from "../utils/numberPill";

import type { AnimationStyle } from "@juiceforward/narrative-schema";
export type { AnimationStyle } from "@juiceforward/narrative-schema";

interface AnimatedTextProps {
  text: string;
  onComplete?: () => void;
  style?: AnimationStyle;
  skipAnimation?: boolean;
  /** Speed multiplier: 1 = normal, 2 = twice as fast, 0.5 = half speed */
  speed?: number;
}

// Fade animation - phrases fade in and slide up
function FadeAnimation({
  text,
  onComplete,
  speed = 1,
}: {
  text: string;
  onComplete?: () => void;
  speed?: number;
}) {
  const d = (ms: number) => Math.max(16, Math.round(ms / speed));

  // Check if entire text is wrapped in italic markers (single asterisks)
  const isFullyItalic =
    text.startsWith("*") &&
    text.endsWith("*") &&
    !text.startsWith("**") &&
    !text.endsWith("**");
  const processedText = isFullyItalic ? text.slice(1, -1) : text;

  const phrases = splitIntoPhrases(processedText);
  const phraseCount = phrases.length;
  const [visibleCount, setVisibleCount] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    setVisibleCount(0);
    const timers: number[] = [];
    const delay = d(ANIMATION_DURATIONS.phraseDelay);

    for (let i = 0; i < phraseCount; i++) {
      timers.push(
        window.setTimeout(() => setVisibleCount(i + 1), (i + 1) * delay),
      );
    }
    timers.push(
      window.setTimeout(
        () => onCompleteRef.current?.(),
        (phraseCount + 1) * delay,
      ),
    );

    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [processedText, phraseCount, speed]); // eslint-disable-line react-hooks/exhaustive-deps

  const transDur = (0.4 / speed).toFixed(2);

  const content = (
    <span>
      {phrases.map((phrase, index) => {
        const isVisible = index < visibleCount;
        const isHeader = parseHeader(phrase.text) !== null;
        return (
          <span
            key={`${processedText.slice(0, 20)}-${index}`}
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "none" : "translateY(12px)",
              transition: `opacity ${transDur}s ease-out, transform ${transDur}s ease-out`,
              display: "inline",
            }}
          >
            {renderTextWithFormatting(phrase.text, 0)}
            {!isHeader && phrase.endsWithNewline ? (
              <br />
            ) : !isHeader && index < phrases.length - 1 ? (
              " "
            ) : (
              ""
            )}
          </span>
        );
      })}
    </span>
  );

  if (isFullyItalic) {
    return (
      <span className="italic" style={{ fontStyle: "italic", opacity: 0.95 }}>
        {content}
      </span>
    );
  }
  return content;
}

// Typewriter animation - characters appear with pauses between phrases
function TypewriterAnimation({
  text,
  onComplete,
  speed = 1,
}: {
  text: string;
  onComplete?: () => void;
  speed?: number;
}) {
  const d = (ms: number) => Math.max(8, Math.round(ms / speed));

  const [visibleChars, setVisibleChars] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const { isMuted } = useAudioContext();
  const isMutedRef = useRef(isMuted);
  isMutedRef.current = isMuted;

  // Check for header at the start - we strip the markers for display
  const headerInfo = useMemo(() => parseHeader(text.split("\n")[0]), [text]);
  // If header exists, remove the header line from text for processing
  const processedText = useMemo(() => {
    if (headerInfo) {
      const lines = text.split("\n");
      lines[0] = headerInfo.content;
      return lines.join("\n");
    }
    return text;
  }, [text, headerInfo]);

  const plainText = useMemo(
    () => stripMarkdownSyntax(processedText),
    [processedText],
  );
  const totalChars = Array.from(plainText).length;

  const pausePoints = useMemo(() => {
    const points: number[] = [];
    const stripped = stripMarkdownSyntax(processedText);
    const regex = /,\s+(?=[A-Za-z])|[.!?]\s+/g;
    let match;
    while ((match = regex.exec(stripped)) !== null) {
      points.push(
        Array.from(stripped.slice(0, match.index + match[0].length)).length,
      );
    }
    return points;
  }, [processedText]);

  useEffect(() => {
    setVisibleChars(0);
    setIsComplete(false);
    let currentChar = 0;
    let timeoutId: number;
    let completeTimeoutId: number | undefined;
    let isStopped = false;

    const typeNextChar = () => {
      if (isStopped) return;

      currentChar++;
      setVisibleChars(currentChar);

      if (!isMutedRef.current) {
        playTypewriterClick();
      }

      if (currentChar >= totalChars) {
        setIsComplete(true);
        completeTimeoutId = window.setTimeout(() => {
          if (!isStopped) onCompleteRef.current?.();
        }, d(300));
        return;
      }

      const isPausePoint = pausePoints.includes(currentChar);
      let delay: number;
      if (isPausePoint) {
        delay = d(ANIMATION_DURATIONS.typewriterPause) + Math.random() * d(150);
      } else {
        const baseDelay = ANIMATION_DURATIONS.typewriterChar;
        const variation = (Math.random() * 30 - 10) / speed;
        delay = Math.max(8, d(baseDelay) + variation);
        if (Math.random() < 0.08) {
          delay += d(50) + Math.random() * d(80);
        }
      }

      timeoutId = window.setTimeout(typeNextChar, delay);
    };

    timeoutId = window.setTimeout(typeNextChar, d(100));

    return () => {
      isStopped = true;
      window.clearTimeout(timeoutId);
      if (completeTimeoutId !== undefined) {
        window.clearTimeout(completeTimeoutId);
      }
    };
  }, [processedText, totalChars, pausePoints, speed]); // eslint-disable-line react-hooks/exhaustive-deps

  const segments = parseInlineMarkdown(processedText);
  let charIndex = 0;
  let isFirstLine = true;
  const headerContentLength = headerInfo ? headerInfo.content.length : 0;

  return (
    <span style={{ position: "relative" }}>
      {renderHighlightRuns(segments, (seg, partIndex) => {
        if (seg.kind === "newline") {
          isFirstLine = false;
          return <br key={partIndex} />;
        }

        const content = seg.content;
        const sectionStartIndex = charIndex;
        const sectionEndIndex = sectionStartIndex + Array.from(content).length;
        const isInHeader =
          headerInfo && isFirstLine && sectionStartIndex < headerContentLength;

        const elements = Array.from(content).map((char, i) => {
          const thisCharIndex = charIndex;
          charIndex++;
          const isVisible = thisCharIndex < visibleChars;
          const isCursorHere =
            thisCharIndex === visibleChars - 1 && !isComplete;

          return (
            <span key={`${partIndex}-${i}`} style={{ position: "relative" }}>
              <span
                style={{
                  opacity: isVisible ? 1 : 0,
                  transition: "opacity 0.02s ease-out",
                }}
              >
                {char}
              </span>
              {isCursorHere && (
                <span
                  className="animate-pulse"
                  style={{
                    position: "absolute",
                    right: "-2px",
                    fontWeight: "normal",
                  }}
                >
                  |
                </span>
              )}
            </span>
          );
        });

        if (seg.kind === "bold-italic") {
          return (
            <span
              key={partIndex}
              className="font-bold italic"
              style={{ fontStyle: "italic" }}
            >
              {elements}
            </span>
          );
        }
        if (seg.kind === "bold") {
          const isNumber = /^(\$)?\d/.test(content);
          const isFullyTyped = visibleChars >= sectionEndIndex;
          const showPill = isNumber && isFullyTyped;
          return (
            <span
              key={partIndex}
              className={
                showPill
                  ? `font-semibold ${NUMBER_PILL_CLASS}`
                  : "font-semibold"
              }
              style={showPill ? numberPillStyle(content) : undefined}
            >
              {elements}
            </span>
          );
        }
        if (seg.kind === "italic") {
          return (
            <span
              key={partIndex}
              className="italic"
              style={{ fontStyle: "italic", opacity: 0.95 }}
            >
              {elements}
            </span>
          );
        }
        if (seg.kind === "strikethrough") {
          return (
            <span
              key={partIndex}
              className="line-through"
              style={{ textDecoration: "line-through" }}
            >
              {elements}
            </span>
          );
        }
        if (seg.kind === "link" && seg.url) {
          const effectiveKind = seg.innerKind || seg.outerKind;
          const styleClass =
            effectiveKind === "bold-italic"
              ? "font-bold italic"
              : effectiveKind === "bold"
                ? "font-bold"
                : effectiveKind === "italic"
                  ? "italic"
                  : effectiveKind === "strikethrough"
                    ? "line-through"
                    : undefined;
          const styleObj =
            effectiveKind === "bold-italic" || effectiveKind === "italic"
              ? { fontStyle: "italic" as const }
              : undefined;
          const wrappedElements = styleClass ? (
            <span className={styleClass} style={styleObj}>
              {elements}
            </span>
          ) : (
            elements
          );
          return (
            <a
              key={partIndex}
              href={seg.url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 decoration-1 hover:opacity-80 transition-opacity"
            >
              {wrappedElements}
            </a>
          );
        }
        if (isInHeader && headerInfo) {
          return (
            <span
              key={partIndex}
              className={getHeaderClassName(headerInfo.level)}
            >
              {elements}
            </span>
          );
        }
        return <span key={partIndex}>{elements}</span>;
      })}
    </span>
  );
}

// No animation - render text immediately
function NoAnimation({
  text,
  onComplete,
}: {
  text: string;
  onComplete?: () => void;
}) {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    onCompleteRef.current?.();
  }, []);

  const isFullyItalic =
    text.startsWith("*") &&
    text.endsWith("*") &&
    !text.startsWith("**") &&
    !text.endsWith("**");
  const processedText = isFullyItalic ? text.slice(1, -1) : text;
  const phrases = splitIntoPhrases(processedText);

  const content = (
    <span className="inline">
      {phrases.map((phrase, index) => {
        const isHeader = parseHeader(phrase.text) !== null;
        return (
          <span key={index} className="inline">
            {renderTextWithFormatting(phrase.text, 0)}
            {!isHeader && phrase.endsWithNewline ? (
              <br />
            ) : !isHeader && index < phrases.length - 1 ? (
              " "
            ) : (
              ""
            )}
          </span>
        );
      })}
    </span>
  );

  if (isFullyItalic) {
    return (
      <span className="italic" style={{ fontStyle: "italic", opacity: 0.95 }}>
        {content}
      </span>
    );
  }
  return content;
}

// word-by-word animation — one word at a time, fade + subtle rise
function WordByWordAnimation({
  text,
  onComplete,
  speed = 1,
}: {
  text: string;
  onComplete?: () => void;
  speed?: number;
}) {
  const d = (ms: number) => Math.max(16, Math.round(ms / speed));

  const isFullyItalic =
    text.startsWith("*") &&
    text.endsWith("*") &&
    !text.startsWith("**") &&
    !text.endsWith("**");
  const processedText = isFullyItalic ? text.slice(1, -1) : text;

  const words = splitIntoWords(processedText);
  const wordCount = words.length;
  const [visibleCount, setVisibleCount] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    setVisibleCount(0);
    const timers: number[] = [];
    const delay = d(ANIMATION_DURATIONS.wordByWordDelay);
    for (let i = 0; i < wordCount; i++) {
      timers.push(
        window.setTimeout(() => setVisibleCount(i + 1), (i + 1) * delay),
      );
    }
    timers.push(
      window.setTimeout(
        () => onCompleteRef.current?.(),
        (wordCount + 1) * delay,
      ),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [processedText, wordCount, speed]); // eslint-disable-line react-hooks/exhaustive-deps

  const transDur = (0.3 / speed).toFixed(2);

  const content = (
    <span>
      {words.map((word, index) => {
        const isVisible = index < visibleCount;
        return (
          <span
            key={index}
            style={{
              display: "inline",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(8px)",
              transition: `opacity ${transDur}s ease-out, transform ${transDur}s ease-out`,
            }}
          >
            {renderTextWithFormatting(word.text, 0)}
            {word.endsWithNewline ? (
              <br />
            ) : index < words.length - 1 ? (
              " "
            ) : (
              ""
            )}
          </span>
        );
      })}
    </span>
  );

  if (isFullyItalic) {
    return (
      <span className="italic" style={{ fontStyle: "italic", opacity: 0.95 }}>
        {content}
      </span>
    );
  }
  return content;
}

// rise animation — phrases slide up from behind a clip mask, theatrical title-card feel
function RiseAnimation({
  text,
  onComplete,
  speed = 1,
}: {
  text: string;
  onComplete?: () => void;
  speed?: number;
}) {
  const d = (ms: number) => Math.max(16, Math.round(ms / speed));

  const isFullyItalic =
    text.startsWith("*") &&
    text.endsWith("*") &&
    !text.startsWith("**") &&
    !text.endsWith("**");
  const processedText = isFullyItalic ? text.slice(1, -1) : text;

  // Rise splits only on newlines, not on commas/em-dashes/sentences. Long body
  // prose rises as one chunk so text can wrap naturally inside; multi-line
  // headlines still get a staged line-by-line rise.
  const phrases = processedText
    .split(/\n+/)
    .filter((line) => line.trim().length > 0)
    .map((line, i, arr) => ({
      text: line,
      endsWithNewline: i < arr.length - 1,
    }));
  const phraseCount = phrases.length;
  const [visibleCount, setVisibleCount] = useState(0);
  const [settledCount, setSettledCount] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const transDurNum = 0.4 / speed;
  const transDur = transDurNum.toFixed(2);

  useEffect(() => {
    setVisibleCount(0);
    setSettledCount(0);
    const timers: number[] = [];
    const delay = d(ANIMATION_DURATIONS.risePhraseDelay);
    const settleMs = Math.ceil(transDurNum * 1000);
    for (let i = 0; i < phraseCount; i++) {
      const showAt = (i + 1) * delay;
      timers.push(window.setTimeout(() => setVisibleCount(i + 1), showAt));
      timers.push(
        window.setTimeout(() => setSettledCount(i + 1), showAt + settleMs),
      );
    }
    timers.push(
      window.setTimeout(
        () => onCompleteRef.current?.(),
        (phraseCount + 1) * delay,
      ),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [processedText, phraseCount, speed]); // eslint-disable-line react-hooks/exhaustive-deps

  const content = (
    <span>
      {phrases.map((phrase, index) => {
        const isVisible = index < visibleCount;
        const isSettled = index < settledCount;
        const isHeader = parseHeader(phrase.text) !== null;
        return (
          <span
            key={index}
            style={{
              display: phrase.endsWithNewline ? "block" : "inline-block",
              overflow: isSettled ? "visible" : "hidden",
              verticalAlign: "bottom",
            }}
          >
            <span
              style={{
                display: "inline-block",
                transform: isVisible ? "translateY(0)" : "translateY(110%)",
                transition: `transform ${transDur}s cubic-bezier(0.16, 1, 0.3, 1)`,
              }}
            >
              {renderTextWithFormatting(phrase.text, 0)}
            </span>
            {!isHeader && !phrase.endsWithNewline && index < phrases.length - 1
              ? " "
              : ""}
          </span>
        );
      })}
    </span>
  );

  if (isFullyItalic) {
    return (
      <span className="italic" style={{ fontStyle: "italic", opacity: 0.95 }}>
        {content}
      </span>
    );
  }
  return content;
}

// flash-in animation — words pop in from a large scale and snap to normal size
function FlashInAnimation({
  text,
  onComplete,
  speed = 1,
}: {
  text: string;
  onComplete?: () => void;
  speed?: number;
}) {
  const d = (ms: number) => Math.max(16, Math.round(ms / speed));

  const isFullyItalic =
    text.startsWith("*") &&
    text.endsWith("*") &&
    !text.startsWith("**") &&
    !text.endsWith("**");
  const processedText = isFullyItalic ? text.slice(1, -1) : text;

  const words = splitIntoWords(processedText);
  const wordCount = words.length;
  const [visibleCount, setVisibleCount] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    setVisibleCount(0);
    const timers: number[] = [];
    const delay = d(ANIMATION_DURATIONS.flashInWordDelay);
    for (let i = 0; i < wordCount; i++) {
      timers.push(
        window.setTimeout(() => setVisibleCount(i + 1), (i + 1) * delay),
      );
    }
    timers.push(
      window.setTimeout(
        () => onCompleteRef.current?.(),
        wordCount * delay + d(300),
      ),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [processedText, wordCount, speed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Snappy pop: fast ease-out-expo, no spring oscillation
  const popDuration = parseFloat((0.22 / speed).toFixed(3));

  const content = (
    <span>
      {words.map((word, index) => {
        const isVisible = index < visibleCount;
        return (
          // Plain inline span — no display:inline-block here so spaces render correctly
          <span key={index}>
            {isVisible ? (
              <motion.span
                key={`${index}-v`}
                initial={{ scale: 6, opacity: 1 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: popDuration, ease: [0.16, 1, 0.3, 1] }}
                style={{
                  display: "inline-block",
                  transformOrigin: "center bottom",
                }}
              >
                {renderTextWithFormatting(word.text, 0)}
              </motion.span>
            ) : (
              <span style={{ opacity: 0, display: "inline-block" }}>
                {renderTextWithFormatting(word.text, 0)}
              </span>
            )}
            {word.endsWithNewline ? (
              <br />
            ) : index < words.length - 1 ? (
              " "
            ) : (
              ""
            )}
          </span>
        );
      })}
    </span>
  );

  if (isFullyItalic) {
    return (
      <span className="italic" style={{ fontStyle: "italic", opacity: 0.95 }}>
        {content}
      </span>
    );
  }
  return content;
}

// Scramble animation — characters revealed left-to-right, each cycling random chars before locking
const SCRAMBLE_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%";

function randomScrambleChar(): string {
  return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
}

function ScrambleAnimation({
  text,
  onComplete,
  speed = 1,
}: {
  text: string;
  onComplete?: () => void;
  speed?: number;
}) {
  const d = (ms: number) => Math.max(8, Math.round(ms / speed));

  const plainText = useMemo(() => stripMarkdownSyntax(text), [text]);
  const plainChars = useMemo(() => Array.from(plainText), [plainText]);
  const charCount = plainChars.length;

  type Slot = { display: string; locked: boolean };
  const [slots, setSlots] = useState<Slot[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const timers = useRef<(number | ReturnType<typeof setInterval>)[]>([]);

  useEffect(() => {
    setSlots([]);
    setIsComplete(false);
    timers.current = [];

    const stagger = d(ANIMATION_DURATIONS.scrambleLetterStagger);
    const lockDelay = d(ANIMATION_DURATIONS.scrambleLockDelay);
    const intervalMs = Math.max(16, Math.round(40 / speed));

    for (let i = 0; i < charCount; i++) {
      const char = plainChars[i];
      const shouldScramble = /[a-zA-Z0-9]/.test(char);

      const revealT = window.setTimeout(() => {
        // Reveal this slot
        setSlots((prev) => [
          ...prev,
          {
            display: shouldScramble ? randomScrambleChar() : char,
            locked: !shouldScramble,
          },
        ]);

        if (shouldScramble) {
          // Cycle random chars for this slot
          const intervalId = setInterval(() => {
            setSlots((prev) => {
              if (i >= prev.length || prev[i].locked) return prev;
              const next = [...prev];
              next[i] = { display: randomScrambleChar(), locked: false };
              return next;
            });
          }, intervalMs);
          timers.current.push(intervalId);

          // Lock to correct char after scramble duration
          const lockT = window.setTimeout(() => {
            clearInterval(intervalId);
            setSlots((prev) => {
              const next = [...prev];
              if (i < next.length) next[i] = { display: char, locked: true };
              return next;
            });
          }, lockDelay);
          timers.current.push(lockT);
        }
      }, i * stagger);
      timers.current.push(revealT);
    }

    // Mark complete after last char locks
    const totalDuration = (charCount - 1) * stagger + lockDelay + d(150);
    const doneT = window.setTimeout(() => {
      setIsComplete(true);
      onCompleteRef.current?.();
    }, totalDuration);
    timers.current.push(doneT);

    return () => {
      timers.current.forEach((id) => {
        clearInterval(id as ReturnType<typeof setInterval>);
        window.clearTimeout(id as number);
      });
    };
  }, [text, charCount, speed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isComplete) {
    // Swap to formatted render once all chars are settled
    const phrases = splitIntoPhrases(text);
    return (
      <span>
        {phrases.map((phrase, index) => (
          <span key={index}>
            {renderTextWithFormatting(phrase.text, 0)}
            {phrase.endsWithNewline ? (
              <br />
            ) : index < phrases.length - 1 ? (
              " "
            ) : (
              ""
            )}
          </span>
        ))}
      </span>
    );
  }

  return (
    <span>
      {slots.map((slot, i) => (
        <span
          key={i}
          style={{
            opacity: slot.locked ? 1 : 0.6,
            color: slot.locked ? "inherit" : "rgba(255,255,255,0.7)",
            transition: "opacity 0.06s, color 0.06s",
            whiteSpace: slot.display === " " ? "pre" : undefined,
          }}
        >
          {slot.display}
        </span>
      ))}
    </span>
  );
}

// blur-in animation — phrases crystallize out of blur, editorial/cinematic feel
function BlurInAnimation({
  text,
  onComplete,
  speed = 1,
}: {
  text: string;
  onComplete?: () => void;
  speed?: number;
}) {
  const d = (ms: number) => Math.max(16, Math.round(ms / speed));

  const isFullyItalic =
    text.startsWith("*") &&
    text.endsWith("*") &&
    !text.startsWith("**") &&
    !text.endsWith("**");
  const processedText = isFullyItalic ? text.slice(1, -1) : text;

  const phrases = splitIntoPhrases(processedText);
  const phraseCount = phrases.length;
  const [visibleCount, setVisibleCount] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    setVisibleCount(0);
    const timers: number[] = [];
    const delay = d(ANIMATION_DURATIONS.blurInPhraseDelay);
    for (let i = 0; i < phraseCount; i++) {
      timers.push(
        window.setTimeout(() => setVisibleCount(i + 1), (i + 1) * delay),
      );
    }
    timers.push(
      window.setTimeout(
        () => onCompleteRef.current?.(),
        (phraseCount + 1) * delay,
      ),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [processedText, phraseCount, speed]); // eslint-disable-line react-hooks/exhaustive-deps

  const transDur = (0.5 / speed).toFixed(2);

  const content = (
    <span>
      {phrases.map((phrase, index) => {
        const isVisible = index < visibleCount;
        const isHeader = parseHeader(phrase.text) !== null;
        return (
          <span
            key={index}
            style={{
              display: "inline",
              opacity: isVisible ? 1 : 0,
              filter: isVisible ? "blur(0px)" : "blur(8px)",
              transition: `opacity ${transDur}s ease-out, filter ${transDur}s ease-out`,
            }}
          >
            {renderTextWithFormatting(phrase.text, 0)}
            {!isHeader && phrase.endsWithNewline ? (
              <br />
            ) : !isHeader && index < phrases.length - 1 ? (
              " "
            ) : (
              ""
            )}
          </span>
        );
      })}
    </span>
  );

  if (isFullyItalic) {
    return (
      <span className="italic" style={{ fontStyle: "italic", opacity: 0.95 }}>
        {content}
      </span>
    );
  }
  return content;
}

// cascade animation — words stream in from the left with tight stagger, flowing/conversational feel
function CascadeAnimation({
  text,
  onComplete,
  speed = 1,
}: {
  text: string;
  onComplete?: () => void;
  speed?: number;
}) {
  const d = (ms: number) => Math.max(16, Math.round(ms / speed));

  const isFullyItalic =
    text.startsWith("*") &&
    text.endsWith("*") &&
    !text.startsWith("**") &&
    !text.endsWith("**");
  const processedText = isFullyItalic ? text.slice(1, -1) : text;

  const words = splitIntoWords(processedText);
  const wordCount = words.length;
  const [visibleCount, setVisibleCount] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    setVisibleCount(0);
    const timers: number[] = [];
    const delay = d(ANIMATION_DURATIONS.cascadeWordDelay);
    for (let i = 0; i < wordCount; i++) {
      timers.push(
        window.setTimeout(() => setVisibleCount(i + 1), (i + 1) * delay),
      );
    }
    timers.push(
      window.setTimeout(
        () => onCompleteRef.current?.(),
        (wordCount + 1) * delay,
      ),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [processedText, wordCount, speed]); // eslint-disable-line react-hooks/exhaustive-deps

  const transDur = (0.25 / speed).toFixed(2);

  const content = (
    <span>
      {words.map((word, index) => {
        const isVisible = index < visibleCount;
        return (
          <span
            key={index}
            style={{
              display: "inline-block",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateX(0)" : "translateX(-20px)",
              transition: `opacity ${transDur}s ease-out, transform ${transDur}s ease-out`,
            }}
          >
            {renderTextWithFormatting(word.text, 0)}
            {word.endsWithNewline ? (
              <br />
            ) : index < words.length - 1 ? (
              "\u00A0"
            ) : (
              ""
            )}
          </span>
        );
      })}
    </span>
  );

  if (isFullyItalic) {
    return (
      <span className="italic" style={{ fontStyle: "italic", opacity: 0.95 }}>
        {content}
      </span>
    );
  }
  return content;
}

export function AnimatedText({
  text,
  onComplete,
  style = "fade",
  skipAnimation = false,
  speed = 1,
}: AnimatedTextProps) {
  const normalizedSpeed = Number.isFinite(speed) && speed > 0 ? speed : 1;
  const prefersReducedMotion = useReducedMotion();

  // Reduced motion: render the full text at once (NoAnimation fires onComplete
  // immediately, so narrative progression / continue-gating still unlocks).
  if (skipAnimation || prefersReducedMotion || style === "none") {
    return <NoAnimation text={text} onComplete={onComplete} />;
  }
  if (style === "typewriter") {
    return (
      <TypewriterAnimation
        text={text}
        onComplete={onComplete}
        speed={normalizedSpeed}
      />
    );
  }
  if (style === "word-by-word") {
    return (
      <WordByWordAnimation
        text={text}
        onComplete={onComplete}
        speed={normalizedSpeed}
      />
    );
  }
  if (style === "rise") {
    return (
      <RiseAnimation
        text={text}
        onComplete={onComplete}
        speed={normalizedSpeed}
      />
    );
  }
  if (style === "flash-in") {
    return (
      <FlashInAnimation
        text={text}
        onComplete={onComplete}
        speed={normalizedSpeed}
      />
    );
  }
  if (style === "scramble") {
    return (
      <ScrambleAnimation
        text={text}
        onComplete={onComplete}
        speed={normalizedSpeed}
      />
    );
  }
  if (style === "blur-in") {
    return (
      <BlurInAnimation
        text={text}
        onComplete={onComplete}
        speed={normalizedSpeed}
      />
    );
  }
  if (style === "cascade") {
    return (
      <CascadeAnimation
        text={text}
        onComplete={onComplete}
        speed={normalizedSpeed}
      />
    );
  }
  return (
    <FadeAnimation
      text={text}
      onComplete={onComplete}
      speed={normalizedSpeed}
    />
  );
}
